import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import CitySelectModal from "../components/CitySelectModal/CitySelectModal";
import { useCities } from "./CitiesContext";
import { getLastCitySlug, setLastCitySlug } from "../utils/userPreferences";
import { trackEvent } from "../utils/analytics";

const CityPickerContext = createContext(null);

export function CityPickerProvider({ children }) {
  const [open, setOpen] = useState(false);
  const { cities, loading } = useCities();
  const location = useLocation();
  const navigate = useNavigate();

  const selectedSlug = useMemo(() => {
    const qs = new URLSearchParams(location.search || "");
    return qs.get("city")?.trim() || getLastCitySlug() || "";
  }, [location.search]);

  const closeCityPicker = useCallback(() => setOpen(false), []);

  const openCityPicker = useCallback(() => {
    trackEvent("city_picker_open", { path: location.pathname });
    setOpen(true);
  }, [location.pathname]);

  const onSelectCity = useCallback(
    (city) => {
      const name = city?.name || "";
      const slug =
        city?.slug ||
        String(name)
          .trim()
          .toLowerCase()
          .replace(/\s+/g, "-") ||
        "";
      if (!slug) return;
      setLastCitySlug(slug);
      const params = new URLSearchParams(location.search || "");
      params.set("city", slug);
      const qs = params.toString();
      navigate(`${location.pathname}${qs ? `?${qs}` : ""}`, { replace: true });
      trackEvent("city_selected", { city_slug: slug, source: "picker_modal" });
      setOpen(false);
    },
    [location.pathname, location.search, navigate]
  );

  const value = useMemo(
    () => ({
      openCityPicker,
      closeCityPicker,
      isCityPickerOpen: open,
    }),
    [open, openCityPicker, closeCityPicker]
  );

  return (
    <CityPickerContext.Provider value={value}>
      {children}
      <CitySelectModal
        show={open}
        onClose={closeCityPicker}
        cities={cities}
        loading={loading}
        selectedSlug={selectedSlug}
        onSelectCity={onSelectCity}
        allowDismiss
        showTitle={false}
      />
    </CityPickerContext.Provider>
  );
}

export function useCityPicker() {
  const ctx = useContext(CityPickerContext);
  if (!ctx) {
    return {
      openCityPicker: () => {},
      closeCityPicker: () => {},
      isCityPickerOpen: false,
    };
  }
  return ctx;
}
